import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Label } from '../Form';
import { ApolloError, useMutation } from '@apollo/client';
import TesterPicker from '../Tester/TesterPicker';
import useNotify from '../../hooks/useNotify';
import { useHistory } from 'react-router-dom';
import Form from '../Form/Form';
import FormColumn from '../Form/FormColumn';
import FormRow from '../Form/FormRow';
import { IdArrayInput } from '../../hooks/useTablePages';
import useMode from '../../hooks/useMode';
import { gql } from '@/modes/company/gql';

const RECREATE_JOBS = gql(`
  mutation RecreateJobs($input: RecreateJobsInput!) {
    recreateJobs(input: $input) {
      id
    }
  }
`);

export type OutboundAssignmentInput = {
  vendorProfileId: string | number;
  due?: number;
};

export type RecreateJobsInput = {
  inboundAssignmentIds: IdArrayInput;
  outboundAssignment?: OutboundAssignmentInput;
};

type AddJobTracModalProps = {
  inboundAssignmentIds: IdArrayInput;
  onClose: (actionTaken: boolean) => void;
};

const RecreateJobsModal: React.FC<AddJobTracModalProps> = ({
  inboundAssignmentIds,
  onClose,
}: AddJobTracModalProps) => {
  const [testerProfileId, setTesterProfileId] = useState<string>();
  const { showError } = useNotify();
  const history = useHistory();
  const mode = useMode();

  const [createJobs] = useMutation<
    { recreateJobs: { id: string }[] },
    {
      input: RecreateJobsInput;
    }
  >(RECREATE_JOBS);

  const save = async () => {
    try {
      if (inboundAssignmentIds.length) {
        const result = await createJobs({
          variables: {
            input: {
              inboundAssignmentIds,
              outboundAssignment: testerProfileId
                ? {
                    vendorProfileId: testerProfileId,
                  }
                : undefined,
            },
          },
        });
        if (result?.data?.recreateJobs?.length) {
          history.push(`/${mode}/jobs/${result?.data?.recreateJobs[0].id}`);
        }
      }
      onClose(true);
    } catch (e) {
      showError('Error creating job', e as ApolloError);
    }
  };

  return (
    <Modal title="Recreate Jobs" onClose={() => onClose(false)}>
      <ModalBody>
        <Form>
          <>
            <FormRow>
              <FormColumn>
                <Label>Assign to (Optional)</Label>
                <TesterPicker
                  testerProfileId={testerProfileId}
                  isClearable
                  where={{
                    status: ['ACTIVE', 'PENDING'],
                  }}
                  onChange={(tester) => setTesterProfileId(tester?.profileId)}
                />
              </FormColumn>
            </FormRow>
          </>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button icon="save" onClick={save}>
          Recreate{' '}
          {inboundAssignmentIds?.length > 1 ? inboundAssignmentIds?.length : ''}{' '}
          Job
          {inboundAssignmentIds?.length > 1 ? 's' : ''}
        </Button>
        <Button
          variant="outline"
          icon="slash-circle"
          onClick={() => onClose(false)}
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

RecreateJobsModal.propTypes = {
  onClose: PropTypes.any,
};

export default RecreateJobsModal;
